import { FC, useMemo } from 'react'
import { generatePath } from 'react-router-dom'
import { push } from 'connected-react-router'

import FacebookPreview from '../PostPreview/Facebook'
import TwitterPreview from '../PostPreview/Twitter'
import LinkedinPreview from '../PostPreview/Linkedin'
import InstagramPreview from '../PostPreview/Instagram'

import { AUTH_ROUTE } from '../../../pages/App/routes'

import { useDispatch, useSelector } from '../../../store/index'
import {
  setSelectedSocialAction,
  setSelectedPostSocialIdAction
} from '../../../store/managePost/slice'
import { selectSocialMediaDataBySocialName } from '../../../selectors/ProjectSelector'

import SocialPostStatus from '../../SocialPostStatus'
import { definePostSocialStatus } from '../../../models/postStatuses'

import { TPostSocialManagePost } from '../../../store/managePost/newTypes'
import { TProject } from '../../../store/project/types'
import { POST_SOCIAL_STATUS, SOCIAL_NETWORK } from '../../../models/post'

import './Socials.scss'
import { OriginalSocialIcons } from '../../UI/Icons/socials'
import { getPublishDate } from '../../../utils/transformSchedule'

interface SocialPostsProps {
  project: TProject
  location: any
  postSocials: TPostSocialManagePost[]
  restrictions: any
}

const SocialPosts: FC<SocialPostsProps> = ({ postSocials, project, location, restrictions }) => {
  const dispatch = useDispatch()
  const { selectedSocial, socialDetails, schedule,gridPosts } = useSelector((state) => {
    return {
      selectedSocial: state.managePost.selectedSocial,
      socialDetails: selectSocialMediaDataBySocialName(state),
      schedule: state.managePost.latestSchedule,
      gridPosts: state.gridView[SOCIAL_NETWORK.INSTAGRAM_SOCIAL].latestPosts,
      managePost:state.managePost,
    }
  })
  const generatePathToSocial = (social: string) =>
    generatePath(AUTH_ROUTE.MANAGE_POST, {
      projectId: project.id,
      social
    }) + location.search

  const onSocialChange = (social: SOCIAL_NETWORK, id: number) => {
    dispatch(push(generatePathToSocial(social)))
    dispatch(setSelectedSocialAction(social))
    dispatch(setSelectedPostSocialIdAction(id))
  }

  const publishDate = useMemo(() => getPublishDate(schedule), [schedule])

  const socialsToRender = useMemo(() => {
    return postSocials.reduce((result: any, postSocial: any) => {
      result[postSocial.social_network] = postSocial
      return result
    }, {})
  }, [postSocials])

  const displayPreview = (social: string) => {
    let preview = null

    const previewProps = {
      isMokup: true,
      restrictions,
      socialDetails,
      latestSchedule:schedule
    }

    switch (social) {
      case SOCIAL_NETWORK.FACEBOOK_SOCIAL:
        preview = (
          <FacebookPreview
            {
            ...{
              socialPost: socialsToRender[SOCIAL_NETWORK.FACEBOOK_SOCIAL],
              publishDate: publishDate[SOCIAL_NETWORK.FACEBOOK_SOCIAL],
              ...previewProps
            }}
          />
        )
        break
      case SOCIAL_NETWORK.TWITTER_SOCIAL:
        preview = (
          <TwitterPreview
            {
            ...{
              socialPost: socialsToRender[SOCIAL_NETWORK.TWITTER_SOCIAL],
              publishDate: publishDate[SOCIAL_NETWORK.TWITTER_SOCIAL],
              ...previewProps
            }
            }
          />
        )
        break
      case 'linkedin':
        preview = (
          <LinkedinPreview
            {
            ...{
              socialPost: socialsToRender[SOCIAL_NETWORK.LINKEDIN_SOCIAL],
              publishDate: publishDate[SOCIAL_NETWORK.LINKEDIN_SOCIAL],
              ...previewProps
            }}
          />
        )
        break
      case 'instagram':
        preview = (
          <InstagramPreview
            {
            ...{
              socialPost: socialsToRender[SOCIAL_NETWORK.INSTAGRAM_SOCIAL],
              publishDate: publishDate[SOCIAL_NETWORK.INSTAGRAM_SOCIAL],
              posts: gridPosts,
              post: null,
              ...previewProps
            }}
          />
        )
        break
    }

    return preview
  }

  return (
    <div className='social-posts'>
      <div className='social-posts__items-wrapper'>
        <div className='social-posts__items-wrapper__border-placeholder' />
        <div className='social-posts__scroll-wrapper'>
          {postSocials.map(({ social_network, status, id }) => (
            <div
              key={`post-creation-${social_network}-preview`}
              onClick={() => onSocialChange(social_network, id)}
              className={`
                social-posts__scroll-wrapper__item status-${status}
                ${selectedSocial === social_network && 'active'}
              `}
            >
              <div className='item-backdrop'></div>
              <div className={`post-preview-wrapper`}>
                <div className='post-preview-wrapper__social-icon'>
                  {OriginalSocialIcons[social_network]}
                </div>
                <div className='post-preview-wrapper__content'>
                  {displayPreview(social_network)}
                </div>
                <SocialPostStatus className='post-preview-wrapper__post-status' status={status} />
              </div>
            </div>
          ))}

          <div className='social-posts__scroll-wrapper__placeholder' />
        </div>
        <div className='social-posts__scroll-wrapper__border-placeholder' />
      </div>
    </div>
  )
}

export default SocialPosts
